import { Injectable } from '@angular/core';
import { convert as convertNumberToWordsRu } from 'number-to-words-ru';
import { RtsVehicle } from 'src/app/dto/get-rts-result.dto';
import { Order } from '../../dto/order.dto';
import { ExcelGeneratorData } from '../../dto/excel-generation-data.dto';
import { XlsxTemplateFillerServiceService } from './xlsx-template-filler-service.service';
import { DateTimeService } from './date-time.service';
import { TTN_TEMPLATE_TPL_PATH } from '../../utils/constants/document-template.constants';
import { DocumentFieldFormatter } from '../../utils/document-field-formatter.util';

@Injectable({
  providedIn: 'root'
})
export class TtnGeneratorService {
  constructor(
    private xlsxTemplateFillerServiceService: XlsxTemplateFillerServiceService,
    private dateTimeService: DateTimeService
  ) {}

  async getTtn(order: Order, carInfo: RtsVehicle, currentDayId: string): Promise<void> {
    const data = await this.generateTtnDataStructure(order, carInfo, currentDayId);
    await this.xlsxTemplateFillerServiceService.generateAndOpenNewExcelFile(
      TTN_TEMPLATE_TPL_PATH,
      data,
      `ТТН-${order.id}`
    );
  }

  /**
   * Возвращает номер ТТН для рейса. Номер — полностью клиентский (сервер не авторитет).
   *
   * Идемпотентность на рейс: если у рейса уже есть номер — возвращаем его без изменений (тот же
   * физический документ обязан сохранять номер, он мог быть распечатан/подписан). Иначе присваиваем
   * следующий порядковый номер дня как `max(уже присвоенных в этом дне) + 1` — именно max, а НЕ длину
   * списка: удалённый рейс оставляет пропуск в нумерации, и его номер больше не переиспользуется. Это
   * даёт уникальность на рейс (D10): раньше хвост брался от позиции машины в списке, и при удалении/
   * пересортировке разные рейсы получали один номер.
   *
   * Формат: `${orderId}-${MM}${DD}${NN}` (NN — порядковый номер рейса за день, минимум 2 цифры).
   *
   * @param orderId - Идентификатор заказа.
   * @param shipmentDate - Дата отгрузки дня (общая для всех рейсов дня).
   * @param dayVehicles - Все рейсы этого дня (для вычисления max присвоенных номеров).
   * @param targetVehicleId - Идентификатор целевого рейса.
   */
  public resolveTtnId(
    orderId: number,
    shipmentDate: Date,
    dayVehicles: RtsVehicle[],
    targetVehicleId: string
  ): string {
    const target = dayVehicles.find(
      v => v.id?.toString() === targetVehicleId?.toString()
    );
    const existing = target?.loadingData?.ttnId?.trim();
    if (existing) {
      return existing;
    }

    const mm = (shipmentDate.getMonth() + 1).toString().padStart(2, '0');
    const dd = shipmentDate.getDate().toString().padStart(2, '0');
    const prefix = `${orderId}-${mm}${dd}`;

    const maxAssignedSeq = dayVehicles.reduce((max, v) => {
      const seq = this.parseTtnSeq(v.loadingData?.ttnId, prefix);
      return seq > max ? seq : max;
    }, 0);

    return `${prefix}${(maxAssignedSeq + 1).toString().padStart(2, '0')}`;
  }

  public async generateTtnDataStructure(order: Order, carInfo: RtsVehicle, currentDayId: string) {
    const generateTtnData: ExcelGeneratorData = {
      cellDataArray: []
    };

    generateTtnData.cellDataArray.push({
      address: 'AA5',
      value: carInfo.loadingData.ttnId,
      name: 'id',
      pageNumber: 1
    });

    generateTtnData.cellDataArray.push({
      address: 'AK9',
      value: order.forwarder?.okpo,
      name: 'okpo',
      pageNumber: 1
    });

    // Use shipment date by currentDayId and delegate parsing to DateTimeService
    const ttnDay = order?.getDayById?.(currentDayId);
    const rawDate = ttnDay?.shipmentDate ?? this.dateTimeService.getNowDateStringFormat();
    const { day: dd, month: mm, year: yyyy, monthWord, yearShort } = this.dateTimeService.parseDateParts(rawDate);

    generateTtnData.cellDataArray.push({
      address: 'I6',
      value: dd,
      name: 'day',
      pageNumber: 1
    });
    generateTtnData.cellDataArray.push({
      address: 'L6',
      value: monthWord,
      name: 'month',
      pageNumber: 1
    });

    generateTtnData.cellDataArray.push({
      address: 'V6',
      value: yearShort,
      name: 'year',
      pageNumber: 1
    });

    generateTtnData.cellDataArray.push({
      address: 'AK7',
      value: dd,
      name: 'create-day',
      pageNumber: 1
    });
    generateTtnData.cellDataArray.push({
      address: 'AL7',
      value: mm,
      name: 'create-month',
      pageNumber: 1
    });

    generateTtnData.cellDataArray.push({
      address: 'AO7',
      value: yyyy,
      name: 'year',
      pageNumber: 1
    });

    if (order.forwarder) {
      const forwarderData = DocumentFieldFormatter.safeConcatenate(
        [
          { value: order.forwarder.orgName },
          { value: order.forwarder.inn },
          { value: order.forwarder.address },
          { value: order.forwarder.phone, prefix: 'тел. ' }
        ],
        ' '
      );

      generateTtnData.cellDataArray.push({
        address: 'F9',
        value: forwarderData,
        name: 'expeditor-data',
        pageNumber: 1
      });

      const forwarderData2 =
        DocumentFieldFormatter.safeConcatenate(
          [
            { value: order.forwarder.orgName },
            { value: order.forwarder.inn },
            { value: order.forwarder.address }
          ],
          ', '
        ) +
        DocumentFieldFormatter.safeConcatenate(
          [{ value: order.forwarder.phone, prefix: '; тел. ' }],
          ''
        );

      generateTtnData.cellDataArray.push({
        address: 'H21',
        value: forwarderData2,
        name: 'expeditor-data2',
        pageNumber: 1
      });
    }

    if (carInfo.model) {
      generateTtnData.cellDataArray.push({
        address: 'H10',
        value: DocumentFieldFormatter.safeValue(carInfo.model),
        name: 'car-name',
        pageNumber: 1
      });
    }

    if (carInfo.registrationNumber) {
      generateTtnData.cellDataArray.push({
        address: 'V10',
        value: DocumentFieldFormatter.safeValue(carInfo.registrationNumber),
        name: 'car-number',
        pageNumber: 1
      });
    }

    if (carInfo) {
      const ownerData = DocumentFieldFormatter.safeConcatenate(
        [
          { value: carInfo.carrierName, suffix: ',' },
          { value: carInfo.carrierInn, prefix: '(ИНН ', suffix: '),' },
          { value: carInfo.carrierAddress, suffix: ',' },
          { value: carInfo.driverPhone, prefix: 'тел.:' }
        ],
        ' '
      );

      generateTtnData.cellDataArray.push({
        address: 'O12',
        value: ownerData,
        name: 'owner-info',
        pageNumber: 1
      });
    }

    if (carInfo.driverName) {
      generateTtnData.cellDataArray.push({
        address: 'E15',
        value: DocumentFieldFormatter.safeValue(carInfo.driverName),
        name: 'driver-name',
        pageNumber: 1
      });
    }

    if (order.buyer) {
      const buyerData = DocumentFieldFormatter.safeConcatenate(
        [
          { value: order.buyer?.orgName },
          { value: order.buyer?.inn },
          { value: order.buyer?.address },
          { value: order.buyer?.phone, prefix: 'тел. ' }
        ],
        ' '
      );

      generateTtnData.cellDataArray.push({
        address: 'F18',
        value: buyerData,
        name: 'buyer-data',
        pageNumber: 1
      });

      generateTtnData.cellDataArray.push({
        address: 'H29',
        value: buyerData,
        name: 'buyer-data',
        pageNumber: 1
      });
    }

    if (order.loading.address) {
      generateTtnData.cellDataArray.push({
        address: 'E25',
        value: DocumentFieldFormatter.safeValue(order.loading.address),
        name: 'seller-location',
        pageNumber: 1
      });
    }

    if (order.unloading.address) {
      generateTtnData.cellDataArray.push({
        address: 'F32',
        value: DocumentFieldFormatter.safeValue(order.unloading.address),
        name: 'buyer-location',
        pageNumber: 1
      });
    }

    if (carInfo.trailerRegistrationNumber) {
      generateTtnData.cellDataArray.push({
        address: 'U36',
        value: DocumentFieldFormatter.safeValue(
          carInfo.trailerRegistrationNumber
        ),
        name: 'trail-number',
        pageNumber: 1
      });
    }

    if (order.freightName) {
      // Год урожая добавляется, только если он задан: «Пшеница 4 класс, год урожая 2025» или «Пшеница 4 класс».
      generateTtnData.cellDataArray.push({
        address: 'F42',
        value: DocumentFieldFormatter.ttnFreight(order.freightName, order.cropYear),
        name: 'product-data',
        pageNumber: 1
      });
    }

    if (order.freightName) {
      const freightQuality = order.freightName
        ? order.freightName.split(' ').slice(1).join(' ')
        : '';
      generateTtnData.cellDataArray.push({
        address: 'F45',
        value: DocumentFieldFormatter.safeValue(freightQuality),
        name: 'product-quality',
        pageNumber: 1
      });
    }

    generateTtnData.cellDataArray.push({
      address: 'R50',
      value: `${carInfo.loadingData.grossWeight.toLocaleString('ru-RU')}`,
      name: 'gross-weight',
      pageNumber: 1
    });

    generateTtnData.cellDataArray.push({
      address: 'V50',
      value: `${carInfo.loadingData.tareWeight.toLocaleString('ru-RU')}`,
      name: 'tare-weight',
      pageNumber: 1
    });

    generateTtnData.cellDataArray.push({
      address: 'Z50',
      value: `${carInfo.loadingData.netWeight.toLocaleString('ru-RU')}`,
      name: 'net-weight',
      pageNumber: 1
    });

    generateTtnData.cellDataArray.push({
      address: 'AB67',
      value: convertNumberToWordsRu(carInfo.loadingData.netWeight, {
        showNumberParts: {
          fractional: false
        },
        showCurrency: {
          integer: false
        }
      }).toLocaleLowerCase(),
      name: 'net-weight-words',
      pageNumber: 1
    });

    if (order.forwarder?.orgName) {
      generateTtnData.cellDataArray.push({
        address: 'AF64',
        value: DocumentFieldFormatter.safeValue(order.forwarder.orgName),
        name: 'forwarder-name',
        pageNumber: 1
      });

      generateTtnData.cellDataArray.push({
        address: 'O70',
        value: DocumentFieldFormatter.safeValue(order.forwarder.orgName),
        name: 'forwarder-name',
        pageNumber: 1
      });
    }

    if (carInfo.driverName) {
      generateTtnData.cellDataArray.push({
        address: 'AK70',
        value: DocumentFieldFormatter.safeValue(carInfo.driverName),
        name: 'driver-name',
        pageNumber: 1
      });
    }

    const additionalDocsData = DocumentFieldFormatter.safeConcatenate(
      [
        {
          value: carInfo.loadingData.declarationOfConformityNumber,
          prefix: 'Декларация о соответствии № ',
          suffix: ` от ${this.dateTimeService.getDateFromTimestamp(carInfo.loadingData.declarationOfConformityDate)}`
        },
        {
          value: carInfo.loadingData.quarantineCertificateNumber,
          prefix: 'Карантинный сертификат ',
          suffix: ` от ${this.dateTimeService.getDateFromTimestamp(carInfo.loadingData.quarantineCertificateDate)}`
        },
        {
          value: carInfo.loadingData.sdizNumber,
          prefix: 'СДИЗ '
        }
      ],
      ', '
    );

    generateTtnData.cellDataArray.push({
      address: 'F73',
      value: additionalDocsData,
      name: 'additional-docs',
      pageNumber: 1
    });

    if (order.forwarder?.orgName) {
      generateTtnData.cellDataArray.push({
        address: 'F16',
        value: DocumentFieldFormatter.safeValue(order.forwarder.orgName),
        name: 'forwarder-name',
        pageNumber: 2
      });
    }

    //исправлено: изменен адрес ячейки для buyer.orgName
    if (order.buyer?.orgName) {
      generateTtnData.cellDataArray.push({
        address: 'F18',
        value: DocumentFieldFormatter.safeValue(order.buyer.orgName),
        name: 'buyer-name',
        pageNumber: 2
      });
    }

    order.generateTtnActions(generateTtnData.cellDataArray);

    return generateTtnData;
  }

  /**
   * Извлекает порядковый номер из номера ТТН, если он относится к тому же дню (совпадает префикс
   * `${orderId}-${MM}${DD}` фиксированной ширины). Иначе — 0 (номер другого дня, серверный номер
   * иного формата или пусто в вычислении max не участвуют).
   */
  private parseTtnSeq(ttnId: string | undefined, prefix: string): number {
    if (!ttnId || !ttnId.startsWith(prefix)) {
      return 0;
    }
    const seq = parseInt(ttnId.slice(prefix.length), 10);
    return Number.isNaN(seq) ? 0 : seq;
  }
}
